import React from 'react';
import {IListConfigColumn} from '@src/@interfaces/components/List/IListConfigColumn';
import {IListConfigNumberColumn} from '@src/@interfaces/components/List/IListConfigNumberColumn';
import {IListConfigImageColumn} from '@src/@interfaces/components/List/IListConfigImageColumn';
import {IListConfigSwitchColumn} from '@src/@interfaces/components/List/IListConfigSwitchColumn';
import {IListConfigSummaryColumn} from '@src/@interfaces/components/List/IListConfigSummaryColumn';
import {IListConfigDatetimeColumn} from '@src/@interfaces/components/List/IListConfigDatetimeColumn';
import {IListConfigDateColumn} from '@src/@interfaces/components/List/IListConfigDateColumn';
import {IListConfigTimeColumn} from '@src/@interfaces/components/List/IListConfigTimeColumn';
import {IListConfigSelectColumn} from '@src/@interfaces/components/List/IListConfigSelectColumn';
import {IListConfigSelectableColumn} from '@src/@interfaces/components/List/IListConfigSelectableColumn';
import {IListConfigRelationColumn} from '@src/@interfaces/components/List/IListConfigRelationColumn';
import {IListPaginationHttpResponse} from '@src/@interfaces/components/List/IListPaginationHttpResponse';
import {IListNPlus1PaginationHttpResponse} from '@src/@interfaces/components/List/IListNPlus1PaginationHttpResponse';
import {IListConfig} from '@src/@interfaces/components/List/IListConfig';
import {IListConfigContext} from '@src/@interfaces/contexts/IListConfigContext';
import {IListColumnProps} from '@src/@interfaces/components/List/IListColumnProps';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {UseQueryResult} from '@tanstack/react-query';
import {TableProps} from 'reactstrap/types/lib/Table';
import {IModelFilter} from '@src/@interfaces/models/ModelFilter/IModelFilter';
import {WithRouterApiProps} from '@src/@types/hocs/WithRouterApiProps';

export enum ListConfigFieldTypes {
    'CUSTOM' = 'custom',
    'TEXT' = 'text',
    'SENSITIVE' = 'sensitive',
    'NUMBER' = 'number',
    'PRICE' = 'price',
    'IMAGE' = 'image',
    'SWITCH' = 'switch',
    'SUMMARY' = 'summary',
    'DATETIME' = 'datetime',
    'DATE' = 'date',
    'TIME' = 'time',
    'TIME_SINCE' = 'time_since',
    'TIME_TENSE' = 'time_tense',
    'SELECT' = 'select',
    'SELECTABLE' = 'selectable',
    'RELATION' = 'relation',
    'COLOR_PICKER' = 'color_picker',
}

export type ListItemControlsAvailableActions = 'delete' | 'restore' | 'edit';

export type ListConfigColumn =
    IListConfigColumn
    | IListConfigNumberColumn
    | IListConfigImageColumn
    | IListConfigSwitchColumn
    | IListConfigSummaryColumn
    | IListConfigDatetimeColumn
    | IListConfigDateColumn
    | IListConfigTimeColumn
    | IListConfigSelectColumn
    | IListConfigSelectableColumn
    | IListConfigRelationColumn;

export type ListConfigNamedColumn<Column = ListConfigColumn> = Column & {
    name: string;
};

export type ListPaginationHttpResponse<Model = any> = {
    allowedFilters?: IModelFilter[],
} & (IListPaginationHttpResponse<Model> | IListNPlus1PaginationHttpResponse<Model>);

export type ListProps<Model extends IEmptyModel = IEmptyModel> =
    {
        controllerName: string,
        config: IListConfig,
        getListRequestUrl?: string,
        recordsProviderAdditionalShapeProps?: Record<string, any>,
        listConfigContextOptions?: Partial<IListConfigContext<Model>>,
        onRowClicked?: (index: number, model: Model, isMiddleButton: boolean, url: {
            location: WithRouterApiProps['urlLocation'],
            searchParams: WithRouterApiProps['urlSearchParams'][0],
        }) => void,
        forceDisplayFilters?: boolean,
        defaultDisplayFilters?: boolean,
    }
    & Partial<Omit<ListContentProps<Model>, 'onRowClicked' | 'uniqueListName'>>
    & Pick<ListContentProps<Model>, 'uniqueListName'>
    & Pick<ListFiltersProps, 'forceColumnsValues' | 'disabledColumns' | 'chosenFilterName' | 'chosenFilterId' | 'dontChangeUserPreferences'>;

export type ListColumnsFactoryComponent<Model extends IEmptyModel = IEmptyModel> = React.ComponentType<
    IListColumnProps<
        ListConfigNamedColumn<any>,
        Model
    >
>;

export type ListState = {
    filtersDisplayed: boolean,
    disableFiltering: boolean,
    currentPage?: number,
    perPage?: number | null,
    searchTerm?: string | null,
    sortBy?: ListSortSettings,
}

export type TableHeadTHPropsProvider = (
    column: ListConfigNamedColumn,
    cellIndex: number,
) => React.HTMLAttributes<HTMLTableHeaderCellElement>;

export type ListTableHeadProps = {
    sortBy?: ListSortSettings,
    saveSortingParams?: (sortingParams?: ListSortSettings) => void,
    columns?: IListConfigContext['columns'],
    uniqueListName: string,
    listSettingsControllerName?: string,
    refreshRecords: () => void,
    theadProps?: React.HTMLAttributes<HTMLTableSectionElement>,
    getTHProps?: React.HTMLAttributes<HTMLTableHeaderCellElement> | TableHeadTHPropsProvider,
    cellBeforeColumns?: React.ComponentType<Record<string, never>> | React.ReactNode,
    additionalControlNode?: React.ReactNode,
    controlsTDProps?: React.HTMLAttributes<HTMLTableDataCellElement>,
    controlsWrapperProps?: React.HTMLAttributes<HTMLDivElement>,
    withoutListSettings?: boolean,
    preferenceNameMutator?: (name: string) => string,
};

export type ListTableHeadState = Pick<ListTableHeadProps, 'sortBy'>;

export type TableBodyMouseEventHandler<Model = any> = (e: React.MouseEvent, model: Model | undefined, rowIndex: number) => void;

export type TableBodyTDPropsProvider<Model = any> = (
    column: ListConfigNamedColumn,
    record: Model | undefined,
    rowIndex: number,
    cellIndex: number,
) => React.HTMLAttributes<HTMLTableDataCellElement>;

export type TableBodyTRPropsProvider<Model = any> = (
    record: Model | undefined,
    rowIndex: number,
) => React.HTMLAttributes<HTMLTableRowElement>;

export type TableBodyTRKeyValueProvider<Model = any> = (
    record: Model | undefined,
    rowIndex: number,
) => string;

export type TableBodyControlsTDPropsProvider<Model = any> = (
    record: Model | undefined,
    rowIndex: number
) => React.HTMLAttributes<HTMLTableDataCellElement>;

export type TableBodyProps<Model extends IEmptyModel = IEmptyModel> = {
    tbodyProps?: React.HTMLAttributes<HTMLTableSectionElement>,
    uniqueListName: string,
    columns?: IListConfigContext['columns'],
    records?: Model[],
    nowLoading?: boolean,
    isPaused?: boolean,
    error?: Error | null,
    onLeftClick?: TableBodyMouseEventHandler<Model>,
    onMiddleClick?: TableBodyMouseEventHandler<Model>,
    onRightClick?: TableBodyMouseEventHandler<Model>,
    canInteractWithRows: boolean,
    refreshRecords: () => void,
    cellBeforeColumnsComponent?: React.ComponentType<{ model: Model | undefined, rowIndex: number }>,
    getTRProps?: React.HTMLAttributes<HTMLTableRowElement> | TableBodyTRPropsProvider<Model>,
    getTDProps?: React.HTMLAttributes<HTMLTableDataCellElement> | TableBodyTDPropsProvider<Model>,
    getTRKeyValue?: TableBodyTRKeyValueProvider<Model>;
    getControlsTDProps?: React.HTMLAttributes<HTMLTableDataCellElement> | TableBodyControlsTDPropsProvider<Model>,
    renderRow?: (
        properties: React.HTMLAttributes<HTMLTableRowElement> & {
            recordId: string | null,
            rowIndex: number,
            context?: IListConfigContext<Model>,
            model?: IListColumnProps<ListConfigNamedColumn, Model>['model'],
            onMouseDown: React.MouseEventHandler,
            onClick: React.MouseEventHandler,
        },
        baseRenderer: (
            extraProps?: React.HTMLAttributes<HTMLTableRowElement>,
            baseContentRenderer?: TableBodyProps<Model>['renderRowContent'],
        ) => React.ReactNode,
        baseContentRenderer: () => React.ReactNode
    ) => React.ReactNode;
    renderRowContent?: (
        properties: {
            recordId: string | null,
            rowIndex: number,
            cellBeforeColumnsComponent?: TableBodyProps<Model>['cellBeforeColumnsComponent'],
            context?: IListConfigContext<Model>,
            renderControlsColumn: () => React.ReactNode,
            renderColumns: (contentRenderer?: TableBodyProps<Model>['renderCellContent']) => React.ReactNode,
            renderColumn: (
                column: ListConfigNamedColumn,
                record: Model | undefined,
                rowIndex: number,
                cellIndex: number,
                contentRenderer?: TableBodyProps<Model>['renderCellContent'],
            ) => React.ReactNode,
        },
        baseRenderer: () => React.ReactNode,
    ) => React.ReactNode;
    renderCellContent?: (
        props: IListColumnProps<ListConfigNamedColumn, Model>,
        baseRenderer: () => React.ReactNode,
    ) => React.ReactNode;
    renderControls?: (
        record: Model | undefined,
        rowIndex: number,
        baseOnClickHandler: (requestBody?: Record<string, any>) => Promise<boolean>,
        renderBaseControls: () => React.ReactNode,
    ) => React.ReactNode,
}

export type TableBodyState = {
    recordsErrors: Record<string, string[]>,
    recordsMessages: Record<string, string[]>,
};

export type ListContentProps<Model extends IEmptyModel = IEmptyModel> = {
    queryResult: UseQueryResult<ListPaginationHttpResponse<Model>, Error>,
    uniqueListName: string,
    listSettingsControllerName?: string,
    pageNumberGetParamName: string,
    listOfRecords?: Model[],
    customRefetch?: () => void,
    customPaginationData?: ListPaginationHttpResponse<never>,
    createButton?: React.ReactNode | (() => React.ReactNode),
    searchTerm?: string | null,
    saveSearchTerm: (searchTerm?: string | null) => void,
    sortBy?: ListSortSettings,
    saveSortingParams: (sortBy?: ListSortSettings) => void,
    filtersAreVisible?: boolean,
    toggleFiltersVisibility?: (stat?: boolean | undefined) => void,
    withoutFilters?: boolean,
    canOpenModelShowPage: () => boolean,
    tableProps?: TableProps,
    tableHeadProps?: Partial<Omit<ListTableHeadProps, 'sortBy' | 'saveSortingParams' | 'refreshRecords'>>,
    tableBodyProps?: Partial<Omit<TableBodyProps<Model>, 'nowLoading' | 'error' | 'records' | 'onMiddleClick' | 'onLeftClick' | 'canInteractWithRows' | 'refreshRecords'>>,
    tableCaptionNode?: React.ReactNode,
    renderComponentBeforeTheTopPagination?: React.ComponentType<ListPaginationHttpResponse<Model>>,
    renderComponentAfterTheTopPagination?: React.ComponentType<ListPaginationHttpResponse<Model>>,
    renderComponentBeforeTheBottomPagination?: React.ComponentType<ListPaginationHttpResponse<Model>>,
    renderComponentAfterTheBottomPagination?: React.ComponentType<ListPaginationHttpResponse<Model>>,
    withoutPagination?: boolean,
    withoutSearchField?: boolean,
    withoutControlsRow?: boolean,
    withoutListSettings?: boolean,
    filteringDisabled?: boolean,
    keepWrapperInBox?: boolean,
    isIsolatedComponent?: boolean,
    onRowClicked?: (index: number, model: Model, isMiddleButton: boolean) => void,
    enableFiltering?: () => void,
    isLoading?: boolean,
} & Pick<ListFiltersProps, 'chosenFilterId' | 'chosenFilterName'>;

export type ListSortSettings = {
    column: string,
    direction: 'asc' | 'desc',
};

export type ListFiltersProps = {
    refreshList: () => void,
    toggleFiltersVisibility: (state?: boolean) => void,
    allowedFilters?: ListPaginationHttpResponse['allowedFilters'],
    preferenceNameMutator?: (name: string) => string,
    isFetching?: boolean,
    dontChangeUserPreferences?: boolean,
    forceColumnsValues?: Record<string, any>,
    disabledColumns?: string[],
    chosenFilterName?: string,
    chosenFilterId?: string | number,
};
